// src/lib/errors/ErrorClassifier.ts

export type ErrorCategory =
  | 'network'
  | 'auth'
  | 'business'
  | 'streaming'
  | 'unexpected';

/**
 * Cause exploitable pour choisir un message et une action utilisateur.
 * `category` reste volontairement large pour préserver les consommateurs
 * existants ; `reason` porte le diagnostic utile à l'interface et aux logs.
 */
export type ErrorReason =
  | 'transport_unreachable'
  | 'transport_timeout'
  | 'server_error'
  | 'server_unavailable'
  | 'session_expired'
  | 'forbidden'
  | 'validation_error'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'business_error'
  | 'stream_interrupted'
  | 'invalid_response'
  | 'unexpected';

export interface ClassifiedError {
  category: ErrorCategory;
  reason: ErrorReason;
  /** Code HTTP ou 0 si réseau/JS */
  statusCode?: number;
  /** Message technique original (pour logs) */
  technicalMessage: string;
  /** Une nouvelle tentative peut raisonnablement aboutir. */
  retryable: boolean;
}

const RETRYABLE_REASONS = new Set<ErrorReason>([
  'transport_unreachable',
  'transport_timeout',
  'server_error',
  'server_unavailable',
  'stream_interrupted',
]);

function buildClassification(
  category: ErrorCategory,
  reason: ErrorReason,
  technicalMessage: string,
  statusCode?: number
): ClassifiedError {
  return {
    category,
    reason,
    statusCode,
    technicalMessage,
    retryable: RETRYABLE_REASONS.has(reason),
  };
}

function hasOfflineBrowserState(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function classifyHttpStatus(
  statusCode: number,
  technicalMessage: string
): ClassifiedError {
  if (statusCode === 401) {
    return buildClassification('auth', 'session_expired', technicalMessage, statusCode);
  }
  if (statusCode === 403) {
    return buildClassification('auth', 'forbidden', technicalMessage, statusCode);
  }
  if (statusCode === 400 || statusCode === 422) {
    return buildClassification('business', 'validation_error', technicalMessage, statusCode);
  }
  if (statusCode === 404) {
    return buildClassification('business', 'not_found', technicalMessage, statusCode);
  }
  if (statusCode === 409) {
    return buildClassification('business', 'conflict', technicalMessage, statusCode);
  }
  if (statusCode === 429) {
    return buildClassification('business', 'rate_limited', technicalMessage, statusCode);
  }
  if (statusCode >= 500 && statusCode <= 599) {
    const reason: ErrorReason = [502, 503, 504].includes(statusCode)
      ? 'server_unavailable'
      : 'server_error';
    return buildClassification('network', reason, technicalMessage, statusCode);
  }
  if (statusCode >= 400 && statusCode <= 499) {
    return buildClassification('business', 'business_error', technicalMessage, statusCode);
  }

  return buildClassification('unexpected', 'unexpected', technicalMessage, statusCode);
}

/**
 * Détermine la catégorie et la cause d'une erreur à partir du type et du
 * status HTTP. Le status est prioritaire : une erreur réseau du navigateur ne
 * doit pas masquer une réponse 4xx déjà reçue du backend.
 */
export function classifyError(
  error: unknown,
  statusCode?: number
): ClassifiedError {
  const technicalMessage =
    error instanceof Error ? error.message : String(error);

  if (typeof statusCode === 'number') {
    if (statusCode === 0) {
      return buildClassification(
        'network',
        'transport_unreachable',
        technicalMessage,
        statusCode
      );
    }
    return classifyHttpStatus(statusCode, technicalMessage);
  }

  const normalizedMessage = technicalMessage.toLowerCase();
  const errorName = error instanceof Error ? error.name.toLowerCase() : '';

  // Un timeout produit par AbortSignal.timeout() porte souvent le nom
  // `TimeoutError`, mais certains navigateurs ne conservent que le message.
  if (
    errorName === 'timeouterror' ||
    errorName === 'aborterror' ||
    normalizedMessage.includes('timed out') ||
    normalizedMessage.includes('timeout') ||
    normalizedMessage.includes('deadline exceeded')
  ) {
    return buildClassification('network', 'transport_timeout', technicalMessage);
  }

  if (
    normalizedMessage.includes('websocket') ||
    normalizedMessage.includes('sse') ||
    normalizedMessage.includes('stream')
  ) {
    return buildClassification('streaming', 'stream_interrupted', technicalMessage);
  }

  if (
    normalizedMessage.includes('json') &&
    (normalizedMessage.includes('parse') ||
      normalizedMessage.includes('unexpected token') ||
      normalizedMessage.includes('invalid'))
  ) {
    return buildClassification('unexpected', 'invalid_response', technicalMessage);
  }

  if (
    normalizedMessage.includes('failed to fetch') ||
    normalizedMessage.includes('networkerror') ||
    normalizedMessage.includes('network request failed') ||
    normalizedMessage.includes('load failed') ||
    normalizedMessage.includes('fetch failed') ||
    hasOfflineBrowserState()
  ) {
    return buildClassification('network', 'transport_unreachable', technicalMessage);
  }

  return buildClassification('unexpected', 'unexpected', technicalMessage);
}
